import { Link } from 'react-router-dom';
import { Navigation } from './Navigation';
import { Sparkles, ArrowLeft, Bell } from 'lucide-react';

interface PremiumFeaturePlaceholderProps {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  features?: string[];
  estimatedLaunch?: string;
}

export function PremiumFeaturePlaceholder({
  title,
  description,
  icon: Icon,
  features = [],
  estimatedLaunch,
}: PremiumFeaturePlaceholderProps) {
  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/discover"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Discover</span>
          </Link>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl border-2 border-orange-200 p-8 md:p-12 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full mb-6">
              <Icon className="w-12 h-12 text-white" />
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">{title}</h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">{description}</p>

            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full font-bold text-lg mb-8">
              <Sparkles className="w-5 h-5" />
              Premium Feature
            </div>

            {/* Coming Soon Message */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon!</h2>
              <p className="text-gray-700 mb-4">
                We're working hard to bring you this premium feature. It will be available soon as part of our ResuSwipe Premium plan.
              </p>
              {estimatedLaunch && (
                <p className="text-sm text-gray-600">
                  <strong>Estimated Launch:</strong> {estimatedLaunch}
                </p>
              )}
            </div>

            {/* Features List */}
            {features.length > 0 && (
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-8 text-left">
                <h3 className="text-xl font-bold text-gray-900 mb-4">What to Expect:</h3>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Notify Me */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 mb-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-left">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Get Notified When We Launch
                  </h3>
                  <p className="text-sm text-gray-600">
                    We'll send you a notification as soon as this feature is available.
                  </p>
                </div>
                <button className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg flex items-center gap-2 whitespace-nowrap">
                  <Bell className="w-5 h-5" />
                  Notify Me
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/discover"
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg"
              >
                Explore Other Features
              </Link>
              <Link
                to="/pricing"
                className="px-8 py-4 bg-white text-orange-600 border-2 border-orange-500 rounded-xl font-bold text-lg hover:bg-orange-50 transition-all"
              >
                View Premium Plans
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-12 bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">When will this feature be available?</h3>
                <p className="text-gray-600">
                  {estimatedLaunch
                    ? `We're targeting ${estimatedLaunch} for the initial launch. Sign up for notifications to be the first to know!`
                    : "We're working on bringing this feature to you as soon as possible. Sign up for notifications to stay updated!"}
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Will this be free or paid?</h3>
                <p className="text-gray-600">
                  This will be a premium feature available to ResuSwipe Premium subscribers. We'll announce pricing details closer to launch.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Can I suggest improvements?</h3>
                <p className="text-gray-600">
                  Absolutely! We'd love to hear your feedback. Reach out to our support team with your suggestions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
